/**
 * lib/surfaceLayout.tsx · shared layout primitives for admin surfaces.
 *
 * The signed-in shell (navigation/appShell) gives every surface a BOUNDED flex
 * parent: a `flex:1` area below the 56px topbar, inside a 100vh row. So a
 * surface fills its space with plain `flex:1` — NOT pixel math against
 * useWindowDimensions(). These primitives encode that one correct pattern so no
 * screen hand-rolls `minHeight: height - 56` again (which broke on every
 * untested viewport, e.g. tall external monitors).
 *
 * Two surface shapes:
 *
 *  • Operational / data-dense (Host, Live View, Voice, Chat) → <SurfaceFill>:
 *    a flex column that fills the viewport. Header/chrome stays put; the data
 *    panels scroll INTERNALLY via <ScrollPanel>. This is how pro tablet POS /
 *    queue apps behave — the chrome never scrolls away under your thumb.
 *
 *  • Forms / reading (Operator, Config) → <SurfacePage>: the whole surface
 *    scrolls as one page, but the paper still fills the viewport when content
 *    is short (flexGrow on the content container — no measured min-height).
 *
 * Both center content to a max width and fill the paper background.
 */

import React from 'react';
import { View, ScrollView, Platform } from 'react-native';
import { useTheme } from '../context/themeContext';
import { CONTENT, useResponsive } from './responsive';

/** Web-only: let a flex child actually shrink so its own overflow scrolls
 *  instead of pushing the parent taller (the classic `min-height:0` flexbox fix). */
const webMinZero = Platform.OS === 'web' ? ({ minHeight: 0 as any, minWidth: 0 as any }) : {};

interface FillProps {
  /** Max content width; defaults to the wide data width. */
  maxWidth?: number;
  children: React.ReactNode;
}

/**
 * Operational surface frame. Fills the shell's flex area (flex:1), centers to
 * maxWidth, paints the paper. Children lay out as a flex column; give the
 * scrolling region a <ScrollPanel> (or any `flex:1` View) so it consumes the
 * remaining height and scrolls inside itself.
 */
export function SurfaceFill({ maxWidth = CONTENT.wide, children }: FillProps) {
  const { theme } = useTheme();
  const { colors } = theme;
  const { pad } = useResponsive();
  return (
    // Outer fills the shell area and paints paper edge-to-edge.
    <View style={{ flex: 1, backgroundColor: colors.paper, ...webMinZero }}>
      {/* Centered column capped at maxWidth; flex:1 so it fills height. */}
      <View style={{ flex: 1, width: '100%', maxWidth, alignSelf: 'center', padding: pad, ...webMinZero }}>
        {children}
      </View>
    </View>
  );
}

/**
 * A region that consumes remaining flex height and scrolls internally.
 * Use for the long lists inside an operational surface (queue rows, ledger,
 * call list). `grow` tunes the flex ratio when several sit side by side.
 */
export function ScrollPanel({
  grow = 1,
  style,
  contentStyle,
  children,
}: {
  grow?: number;
  style?: any;
  contentStyle?: any;
  children: React.ReactNode;
}) {
  return (
    <View style={[{ flex: grow, ...webMinZero }, style]}>
      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={contentStyle}
        showsVerticalScrollIndicator
      >
        {children}
      </ScrollView>
    </View>
  );
}

interface PageProps {
  maxWidth?: number;
  children: React.ReactNode;
}

/**
 * Form / reading surface frame. The whole surface scrolls as one page; the
 * paper fills the viewport even when content is short (flexGrow, not a measured
 * min-height). Use for Operator / Config.
 */
export function SurfacePage({ maxWidth = CONTENT.narrow, children }: PageProps) {
  const { theme } = useTheme();
  const { colors } = theme;
  const { pad } = useResponsive();
  return (
    <ScrollView
      style={{ flex: 1, backgroundColor: colors.paper }}
      contentContainerStyle={{
        // flexGrow lets the content container fill the viewport when content is
        // short, so the paper never bottoms out into a void; it has no effect
        // once content exceeds the viewport (then it scrolls normally).
        flexGrow: 1,
        padding: pad,
        paddingBottom: pad + 36,
        width: '100%',
        maxWidth,
        alignSelf: 'center',
      }}
    >
      {children}
    </ScrollView>
  );
}
